import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface CurrencyConversion {
  country_code: string;
  country_name: string;
  currency_code: string;
  currency_symbol: string;
  rate_to_aed: number;
}

interface ProductCountryPrice {
  packaging_option_id: string;
  country_code: string;
  base_price_aed: number | null;
  price: number;
  currency_code: string;
  currency_symbol: string;
}

interface CurrencyContextType {
  selectedCountry: string;
  setSelectedCountry: (country: string) => void;
  conversions: CurrencyConversion[];
  productPrices: ProductCountryPrice[];
  formatPrice: (aedAmount: number) => { primary: string; secondary: string | null };
  formatProductPrice: (packagingOptionId: string, fallbackAedAmount: number) => { primary: string; secondary: string | null };
  getLocalEquivalent: (aedAmount: number) => { amount: number; symbol: string; code: string } | null;
  getProductLocalEquivalent: (packagingOptionId: string, fallbackAedAmount: number) => { amount: number; symbol: string; code: string } | null;
  getProductAedAmount: (packagingOptionId: string, fallbackAedAmount: number) => number;
  loading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const [selectedCountry, setSelectedCountry] = useState<string>('AE');
  const [conversions, setConversions] = useState<CurrencyConversion[]>([]);
  const [productPrices, setProductPrices] = useState<ProductCountryPrice[]>([]);
  const [loading, setLoading] = useState(true);

  // Load conversions and product prices from database
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [conversionsResult, pricesResult] = await Promise.all([
          supabase.from('currency_conversions').select('*'),
          supabase.from('product_country_prices').select('*').eq('is_active', true)
        ]);
        
        if (conversionsResult.error) {
          console.error('Error fetching currency conversions:', conversionsResult.error);
        } else if (conversionsResult.data) {
          setConversions(conversionsResult.data);
        }

        if (pricesResult.error) {
          console.error('Error fetching product prices:', pricesResult.error);
        } else if (pricesResult.data) {
          setProductPrices(pricesResult.data);
        }
      } catch (err) {
        console.error('Error fetching currency data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Load selected country from session storage and listen for changes
  useEffect(() => {
    const loadCountryFromSession = () => {
      const storedQuizData = sessionStorage.getItem('quizData');
      if (storedQuizData) {
        try {
          const quizData = JSON.parse(storedQuizData);
          // Map country name to country code if needed
          const countryMapping: Record<string, string> = {
            'United Arab Emirates': 'AE',
            'Saudi Arabia': 'SA',
            'United States': 'US',
            'United Kingdom': 'GB',
            'India': 'IN',
            'Pakistan': 'PK',
            'Egypt': 'EG',
            'Kuwait': 'KW',
            'Qatar': 'QA',
            'Bahrain': 'BH',
            'Oman': 'OM',
            'Jordan': 'JO',
          };
          
          if (quizData.country) {
            const countryCode = countryMapping[quizData.country] || quizData.country;
            setSelectedCountry(countryCode);
          }
        } catch {
          // Ignore parse errors
        }
      }
    };

    loadCountryFromSession();

    // Listen for storage changes (when checkout updates country)
    const handleStorageChange = () => {
      loadCountryFromSession();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('currencyUpdate', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('currencyUpdate', handleStorageChange);
    };
  }, []);

  const getLocalEquivalent = (aedAmount: number): { amount: number; symbol: string; code: string } | null => {
    if (selectedCountry === 'AE') return null;
    
    const conversion = conversions.find(c => c.country_code === selectedCountry);
    if (!conversion) return null;
    
    // Apply 3% offset to cover exchange rate fluctuations (UAE excluded - uses base rate)
    const localAmount = Math.round(aedAmount * conversion.rate_to_aed * 1.03);
    return {
      amount: localAmount,
      symbol: conversion.currency_symbol,
      code: conversion.currency_code,
    };
  };

  // Get local equivalent using pre-calculated product prices from database
  const getProductLocalEquivalent = (packagingOptionId: string, fallbackAedAmount: number): { amount: number; symbol: string; code: string } | null => {
    if (selectedCountry === 'AE') return null;
    
    // Find pre-calculated price for this product and country
    const productPrice = productPrices.find(
      p => p.packaging_option_id === packagingOptionId && p.country_code === selectedCountry
    );
    
    if (productPrice) {
      return {
        amount: productPrice.price,
        symbol: productPrice.currency_symbol,
        code: productPrice.currency_code,
      };
    }
    
    // Fallback to dynamic calculation if no pre-calculated price exists
    return getLocalEquivalent(fallbackAedAmount);
  };

  const getProductAedAmount = (packagingOptionId: string, fallbackAedAmount: number): number => {
    const productPrice = productPrices.find(
      (p) => p.packaging_option_id === packagingOptionId && p.country_code === selectedCountry
    );

    if (!productPrice) return fallbackAedAmount;

    // UAE uses Base Price directly (no conversion/offset applied)
    // All other countries also use Base Price for AED payment processing
    return productPrice.base_price_aed ?? fallbackAedAmount;
  };

  const formatPrice = (aedAmount: number): { primary: string; secondary: string | null } => {
    const primary = `AED ${aedAmount.toLocaleString()}`;
    
    if (selectedCountry === 'AE') {
      return { primary, secondary: null };
    }
    
    const local = getLocalEquivalent(aedAmount);
    if (!local) {
      return { primary, secondary: null };
    }
    
    const secondary = `≈ ${local.symbol} ${local.amount.toLocaleString()}`;
    return { primary, secondary };
  };

  // Format price using pre-calculated product prices from database
  const formatProductPrice = (packagingOptionId: string, fallbackAedAmount: number): { primary: string; secondary: string | null } => {
    const aedAmount = getProductAedAmount(packagingOptionId, fallbackAedAmount);
    const primary = `AED ${aedAmount.toLocaleString()}`;

    if (selectedCountry === 'AE') {
      return { primary, secondary: null };
    }

    const local = getProductLocalEquivalent(packagingOptionId, fallbackAedAmount);
    if (!local) {
      return { primary, secondary: null };
    }

    const secondary = `≈ ${local.symbol} ${local.amount.toLocaleString()}`;
    return { primary, secondary };
  };

  return (
    <CurrencyContext.Provider value={{
      selectedCountry,
      setSelectedCountry,
      conversions,
      productPrices,
      formatPrice,
      formatProductPrice,
      getLocalEquivalent,
      getProductLocalEquivalent,
      getProductAedAmount,
      loading,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
